/**
 * Two-tab calibration dialog for the PDF measurement layer:
 *   - Standard scale  : pick a preset (architectural / engineering / metric).
 *   - Two-point       : capture two clicks on the page, type the real distance.
 *
 * Two-point capture itself happens on the page; the parent owns that flow and
 * passes the captured points back via `capturedPoints`. The modal stays out of
 * the way during capture (parent should hide it) and re-mounts with the
 * captured points already filled in.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import {
  STANDARD_SCALES,
  getStandardScaleById,
  resolveTwoPointCalibration,
  coerceDisplayUnitForLengthUnit,
  preferredDisplayUnitForLengthUnit,
  isMetricLengthUnit,
  type StandardScale,
  type DisplayUnit,
  type LengthUnit,
} from '../../utils/measurementMath';
import type { PdfMeasurementCalibration } from '../../types';
import './CalibrationModal.css';

export interface CalibrationApplyPayload {
  mode: 'standard' | 'two-point';
  standardScaleId?: string;
  pointA?: { x: number; y: number };
  pointB?: { x: number; y: number };
  realDistance?: number;
  realDistanceUnit?: LengthUnit;
  realPerPdfPoint: number;
  unitSystem: 'imperial' | 'metric';
  baseUnit: LengthUnit;
  displayUnit: DisplayUnit;
  applyToAllPages: boolean;
}

export interface CalibrationModalProps {
  pageNumber: number;
  pageNaturalWidth: number;
  pageNaturalHeight: number;
  numPages: number;
  existing: PdfMeasurementCalibration | null;
  /** Captured two clicks (set by the parent capture flow) — when present, the two-point tab uses them. */
  capturedPoints?: { a: { x: number; y: number }; b: { x: number; y: number } } | null;
  onRequestTwoPointCapture: () => void;
  onApply: (payload: CalibrationApplyPayload) => Promise<void> | void;
  onClose: () => void;
}

const UNIT_OPTIONS: { value: LengthUnit; label: string }[] = [
  { value: 'in', label: 'in' },
  { value: 'ft', label: 'ft' },
  { value: 'mm', label: 'mm' },
  { value: 'cm', label: 'cm' },
  { value: 'm', label: 'm' },
];

const DISPLAY_UNIT_OPTIONS_IMPERIAL: { value: DisplayUnit; label: string }[] = [
  { value: 'ft-in', label: "ft' in\"" },
  { value: 'ft', label: 'ft (decimal)' },
  { value: 'in', label: 'in' },
];
const DISPLAY_UNIT_OPTIONS_METRIC: { value: DisplayUnit; label: string }[] = [
  { value: 'mm', label: 'mm' },
  { value: 'cm', label: 'cm' },
  { value: 'm', label: 'm' },
];

/* Group standard scales for the dropdown */
const SCALE_GROUPS = [
  { label: 'Architectural', items: STANDARD_SCALES.filter(s => s.group === 'Architectural') },
  { label: 'Engineering', items: STANDARD_SCALES.filter(s => s.group === 'Engineering') },
  { label: 'Metric', items: STANDARD_SCALES.filter(s => s.group === 'Metric') },
];

export default function CalibrationModal({
  pageNumber,
  pageNaturalWidth,
  pageNaturalHeight,
  numPages,
  existing,
  capturedPoints,
  onRequestTwoPointCapture,
  onApply,
  onClose,
}: CalibrationModalProps) {
  const initialTab: 'standard' | 'two-point' =
    existing?.mode === 'two-point' || capturedPoints ? 'two-point' : 'standard';
  const [tab, setTab] = useState<'standard' | 'two-point'>(initialTab);
  const [standardScaleId, setStandardScaleId] = useState<string>(
    existing?.standardScaleId || STANDARD_SCALES[3].id, // 1/4" = 1' as a sensible default
  );
  const [realDistance, setRealDistance] = useState<string>(
    existing?.realDistance != null ? String(existing.realDistance) : '',
  );
  const initialRealDistanceUnit: LengthUnit =
    (existing?.realDistanceUnit as LengthUnit) || 'ft';
  const [realDistanceUnit, setRealDistanceUnit] = useState<LengthUnit>(initialRealDistanceUnit);
  const [displayUnit, setDisplayUnit] = useState<DisplayUnit>(() =>
    // Two-point (or re-opening after metric auto-detect) must not keep a stale metric label unit.
    initialTab === 'two-point' || existing?.mode === 'two-point'
      ? coerceDisplayUnitForLengthUnit(initialRealDistanceUnit, existing?.displayUnit)
      : (existing?.displayUnit || 'ft-in'),
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Switch tab when external capturedPoints arrive.
  useEffect(() => {
    if (capturedPoints) setTab('two-point');
  }, [capturedPoints]);

  const currentScale: StandardScale | null = useMemo(
    () => getStandardScaleById(standardScaleId),
    [standardScaleId],
  );

  const isMetricScale = currentScale?.unitSystem === 'metric';
  const displayOptions = isMetricScale ? DISPLAY_UNIT_OPTIONS_METRIC : DISPLAY_UNIT_OPTIONS_IMPERIAL;

  // Auto-snap displayUnit to the picked scale's natural default.
  useEffect(() => {
    if (tab === 'standard' && currentScale) {
      setDisplayUnit(currentScale.defaultDisplayUnit);
    }
  }, [tab, currentScale]);

  // When entering two-point (or after capture remount), drop incompatible leftover display units.
  useEffect(() => {
    if (tab !== 'two-point') return;
    setDisplayUnit(prev => coerceDisplayUnitForLengthUnit(realDistanceUnit, prev));
  }, [tab, realDistanceUnit]);

  const handleRealDistanceUnitChange = (next: LengthUnit) => {
    setRealDistanceUnit(next);
    // Selecting ft/in (or metric) should also drive label units — otherwise a leftover
    // metric displayUnit can remain selected in state while the dropdown looks imperial.
    setDisplayUnit(preferredDisplayUnitForLengthUnit(next));
  };

  const buildStandardPayload = (applyToAllPages: boolean): CalibrationApplyPayload | null => {
    if (!currentScale) return null;
    return {
      mode: 'standard',
      standardScaleId: currentScale.id,
      realPerPdfPoint: currentScale.realPerPdfPoint,
      unitSystem: currentScale.unitSystem,
      baseUnit: currentScale.baseUnit,
      displayUnit,
      applyToAllPages,
    };
  };

  const buildTwoPointPayload = (applyToAllPages: boolean): CalibrationApplyPayload | null => {
    if (!capturedPoints) {
      setError('Capture two points on the page first.');
      return null;
    }
    const num = Number(realDistance);
    if (!Number.isFinite(num) || num <= 0) {
      setError('Enter the real-world distance between the two points.');
      return null;
    }
    const baseUnit: LengthUnit = isMetricLengthUnit(realDistanceUnit)
      ? realDistanceUnit
      : (realDistanceUnit === 'in' ? 'in' : 'ft');
    const realPerPdfPoint = resolveTwoPointCalibration({
      pointA: capturedPoints.a,
      pointB: capturedPoints.b,
      pageW: pageNaturalWidth,
      pageH: pageNaturalHeight,
      realDistance: num,
      realUnit: realDistanceUnit,
      baseUnit,
    });
    if (!Number.isFinite(realPerPdfPoint) || realPerPdfPoint <= 0) {
      setError('Could not compute scale — points are too close together.');
      return null;
    }
    const unitSystem: 'imperial' | 'metric' = isMetricLengthUnit(baseUnit) ? 'metric' : 'imperial';
    const resolvedDisplayUnit = coerceDisplayUnitForLengthUnit(realDistanceUnit, displayUnit);
    return {
      mode: 'two-point',
      pointA: capturedPoints.a,
      pointB: capturedPoints.b,
      realDistance: num,
      realDistanceUnit,
      realPerPdfPoint,
      unitSystem,
      baseUnit,
      displayUnit: resolvedDisplayUnit,
      applyToAllPages,
    };
  };

  const handleApply = async (applyToAllPages: boolean) => {
    setError(null);
    const payload = tab === 'standard' ? buildStandardPayload(applyToAllPages) : buildTwoPointPayload(applyToAllPages);
    if (!payload) return;
    setSaving(true);
    try {
      await onApply(payload);
      onClose();
    } catch (err) {
      console.error('Failed to apply calibration', err);
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="calib-overlay" onClick={onClose}>
      <div className="calib-modal" onClick={e => e.stopPropagation()}>
        <header className="calib-header">
          <h3 className="calib-title">Set scale (page {pageNumber})</h3>
          <button type="button" className="calib-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="calib-tabs">
          <button
            type="button"
            className={`calib-tab ${tab === 'standard' ? 'active' : ''}`}
            onClick={() => { setTab('standard'); setError(null); }}
          >
            Standard scale
          </button>
          <button
            type="button"
            className={`calib-tab ${tab === 'two-point' ? 'active' : ''}`}
            onClick={() => { setTab('two-point'); setError(null); }}
          >
            Two-point
          </button>
        </div>

        <div className="calib-body">
          {tab === 'standard' && (
            <>
              <div className="calib-section-label">Drawing scale</div>
              <p className="calib-help">
                Pick the scale printed in the title block. Distances on the page will be converted using this ratio.
              </p>
              <select
                className="calib-select"
                value={standardScaleId}
                onChange={e => setStandardScaleId(e.target.value)}
              >
                {SCALE_GROUPS.map(group => (
                  <optgroup key={group.label} label={group.label}>
                    {group.items.map(s => (
                      <option key={s.id} value={s.id}>{s.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <div className="calib-display-unit">
                <label htmlFor="calib-disp-1">Display labels in</label>
                <select
                  id="calib-disp-1"
                  value={displayUnit}
                  onChange={e => setDisplayUnit(e.target.value as DisplayUnit)}
                >
                  {displayOptions.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {tab === 'two-point' && (
            <>
              <div className="calib-section-label">Two-point calibration</div>
              <p className="calib-help">
                Click two points on a known dimension (e.g. the ends of a labeled wall), then enter the real distance.
              </p>
              <div className={`calib-twopoint-status ${capturedPoints ? 'captured' : ''}`}>
                {capturedPoints ? (
                  <>
                    <div style={{ fontSize: '0.83rem', fontWeight: 600 }}>Captured</div>
                    <div className="calib-twopoint-row captured">
                      <span>Point A</span>
                      <span>{capturedPoints.a.x.toFixed(1)}, {capturedPoints.a.y.toFixed(1)}</span>
                    </div>
                    <div className="calib-twopoint-row captured">
                      <span>Point B</span>
                      <span>{capturedPoints.b.x.toFixed(1)}, {capturedPoints.b.y.toFixed(1)}</span>
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: '0.83rem' }}>No points captured yet.</div>
                )}
                <div className="calib-actions-inline">
                  <button type="button" className="calib-btn" onClick={onRequestTwoPointCapture}>
                    {capturedPoints ? 'Re-capture' : 'Capture two points on page'}
                  </button>
                </div>
              </div>

              <div className="calib-section-label">Real-world distance</div>
              <div className="calib-row">
                <input
                  className="calib-input"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  placeholder="e.g. 10"
                  value={realDistance}
                  onChange={e => setRealDistance(e.target.value)}
                />
                <select
                  className="calib-unit-select"
                  value={realDistanceUnit}
                  onChange={e => handleRealDistanceUnitChange(e.target.value as LengthUnit)}
                >
                  {UNIT_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div className="calib-display-unit">
                <label htmlFor="calib-disp-2">Display labels in</label>
                <select
                  id="calib-disp-2"
                  value={coerceDisplayUnitForLengthUnit(realDistanceUnit, displayUnit)}
                  onChange={e => setDisplayUnit(e.target.value as DisplayUnit)}
                >
                  {(isMetricLengthUnit(realDistanceUnit)
                    ? DISPLAY_UNIT_OPTIONS_METRIC
                    : DISPLAY_UNIT_OPTIONS_IMPERIAL
                  ).map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {error && <div className="calib-error">{error}</div>}
        </div>

        <footer className="calib-footer">
          <button type="button" className="calib-btn" onClick={onClose}>Cancel</button>
          <div className="calib-footer-actions">
            <button
              type="button"
              className="calib-btn"
              disabled={saving || numPages <= 1}
              onClick={() => handleApply(true)}
              title={numPages <= 1 ? 'Only one page in this PDF' : 'Apply this scale to all pages'}
            >
              Apply to all pages
            </button>
            <button
              type="button"
              className="calib-btn calib-btn--primary"
              disabled={saving}
              onClick={() => handleApply(false)}
            >
              Apply to this page
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
